import { useCallback, useEffect, useMemo, useState } from "react";
import {
  dismissServiceResourceNote,
  listServiceResourceNotes,
  upsertServiceRig,
  upsertServiceResourceNote,
  upsertServiceTech,
} from "@/lib/service/client";
import { isServiceResourceNoteActive } from "@/lib/service/resourceNotesUtils";
import type { ServiceDefinitionScope, ServiceResourceNote, ServiceRig, ServiceTech } from "@/lib/service/types";
import {
  confirmDiscardDirty,
  DEFAULT_PAGE_SIZE,
  paginateList,
  pageCount,
  useDirtyFormGuard,
  type PageSizeOption,
} from "./servicePanelUtils";

export type RigDraft = {
  id?: string;
  designation: string;
  rig_kind: string;
  standard_tech_id: string;
};

export type TechDraft = {
  id?: string;
  display_name: string;
  phone: string;
  skill_tags: string;
};

const BLANK_RIG: RigDraft = { designation: "", rig_kind: "repair_truck", standard_tech_id: "" };
const BLANK_TECH: TechDraft = { display_name: "", phone: "", skill_tags: "" };
const BLANK_NOTE = { body: "", expires_on: "" };

export function useServiceDefinitionResources(args: {
  scope: ServiceDefinitionScope;
  rigs: ServiceRig[];
  techs: ServiceTech[];
  rigOfferingIds: Record<string, string[]>;
  techOfferingIds: Record<string, string[]>;
  resourcesTab: "rigs" | "techs";
  newResourceSignal: number;
  onReload: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const { scope, rigs, techs, rigOfferingIds, techOfferingIds, resourcesTab, newResourceSignal, onReload, onError } =
    args;

  const [selectedRigId, setSelectedRigId] = useState<string | null>(null);
  const [selectedTechId, setSelectedTechId] = useState<string | null>(null);
  const [rigDraft, setRigDraft] = useState<RigDraft>(BLANK_RIG);
  const [techDraft, setTechDraft] = useState<TechDraft>(BLANK_TECH);
  const [rigBaseline, setRigBaseline] = useState(() => JSON.stringify(BLANK_RIG));
  const [techBaseline, setTechBaseline] = useState(() => JSON.stringify(BLANK_TECH));
  const [actionBusy, setActionBusy] = useState(false);
  const [pageSize, setPageSize] = useState<PageSizeOption>(DEFAULT_PAGE_SIZE);
  const [rigPage, setRigPage] = useState(1);
  const [techPage, setTechPage] = useState(1);
  const [notes, setNotes] = useState<ServiceResourceNote[]>([]);
  const [noteDraft, setNoteDraft] = useState(BLANK_NOTE);
  const [noteBaseline, setNoteBaseline] = useState(() => JSON.stringify(BLANK_NOTE));

  const scopedRigs = useMemo(() => {
    if (scope === "general") return rigs;
    return rigs.filter((rig) => (rigOfferingIds[rig.id] ?? []).includes(scope));
  }, [rigs, rigOfferingIds, scope]);

  const scopedTechs = useMemo(() => {
    if (scope === "general") return techs;
    return techs.filter((tech) => (techOfferingIds[tech.id] ?? []).includes(scope));
  }, [techs, techOfferingIds, scope]);

  const pagedRigs = useMemo(() => paginateList(scopedRigs, rigPage, pageSize), [scopedRigs, rigPage, pageSize]);
  const pagedTechs = useMemo(() => paginateList(scopedTechs, techPage, pageSize), [scopedTechs, techPage, pageSize]);
  const rigPages = pageCount(scopedRigs.length, pageSize);
  const techPages = pageCount(scopedTechs.length, pageSize);

  const rigDirty = rigBaseline !== JSON.stringify(rigDraft);
  const techDirty = techBaseline !== JSON.stringify(techDraft);
  const noteDirty = noteBaseline !== JSON.stringify(noteDraft);
  const formDirty = resourcesTab === "rigs" ? rigDirty || noteDirty : techDirty || noteDirty;

  useDirtyFormGuard(formDirty);

  const loadNotes = useCallback(async () => {
    const res = await listServiceResourceNotes();
    if (res.ok) setNotes(res.data.notes);
  }, []);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes, newResourceSignal]);

  useEffect(() => {
    setRigPage(1);
    setTechPage(1);
  }, [scope, pageSize]);

  function resetNoteDraft() {
    setNoteDraft(BLANK_NOTE);
    setNoteBaseline(JSON.stringify(BLANK_NOTE));
  }

  function notesForResource(type: "rig" | "tech", id: string) {
    return notes.filter(
      (n) => n.resource_type === type && n.resource_id === id && isServiceResourceNoteActive(n)
    );
  }

  function findNoteForResource(type: "rig" | "tech", id: string) {
    return notes.find((n) => n.resource_type === type && n.resource_id === id && isServiceResourceNoteActive(n));
  }

  useEffect(() => {
    if (newResourceSignal > 0) {
      setSelectedRigId(null);
      setSelectedTechId(null);
      setRigDraft(BLANK_RIG);
      setTechDraft(BLANK_TECH);
      setRigBaseline(JSON.stringify(BLANK_RIG));
      setTechBaseline(JSON.stringify(BLANK_TECH));
      resetNoteDraft();
    }
  }, [newResourceSignal]);

  const selectRig = useCallback(
    (rig: ServiceRig) => {
      if (selectedRigId === rig.id) {
        if (!confirmDiscardDirty(rigDirty || noteDirty)) return;
        setSelectedRigId(null);
        setRigDraft(BLANK_RIG);
        setRigBaseline(JSON.stringify(BLANK_RIG));
        resetNoteDraft();
        return;
      }
      if (!confirmDiscardDirty(rigDirty || noteDirty)) return;
      const draft: RigDraft = {
        id: rig.id,
        designation: rig.designation,
        rig_kind: rig.rig_kind,
        standard_tech_id: rig.standard_tech_id ?? "",
      };
      setSelectedRigId(rig.id);
      setRigDraft(draft);
      setRigBaseline(JSON.stringify(draft));
      const existing = findNoteForResource("rig", rig.id);
      const nd = { body: existing?.body ?? "", expires_on: existing?.expires_on ?? "" };
      setNoteDraft(nd);
      setNoteBaseline(JSON.stringify(nd));
    },
    [noteDirty, notes, rigDirty, selectedRigId]
  );

  const selectTech = useCallback(
    (tech: ServiceTech) => {
      if (selectedTechId === tech.id) {
        if (!confirmDiscardDirty(techDirty || noteDirty)) return;
        setSelectedTechId(null);
        setTechDraft(BLANK_TECH);
        setTechBaseline(JSON.stringify(BLANK_TECH));
        resetNoteDraft();
        return;
      }
      if (!confirmDiscardDirty(techDirty || noteDirty)) return;
      const draft: TechDraft = {
        id: tech.id,
        display_name: tech.display_name,
        phone: tech.phone,
        skill_tags: tech.skill_tags.join(", "),
      };
      setSelectedTechId(tech.id);
      setTechDraft(draft);
      setTechBaseline(JSON.stringify(draft));
      const existing = findNoteForResource("tech", tech.id);
      const nd = { body: existing?.body ?? "", expires_on: existing?.expires_on ?? "" };
      setNoteDraft(nd);
      setNoteBaseline(JSON.stringify(nd));
    },
    [noteDirty, notes, selectedTechId, techDirty]
  );

  const saveRig = useCallback(async () => {
    if (!rigDraft.designation.trim()) {
      onError("Rig designation is required.");
      return;
    }
    setActionBusy(true);
    onError(null);
    const res = await upsertServiceRig({
      id: rigDraft.id,
      designation: rigDraft.designation.trim(),
      rig_kind: rigDraft.rig_kind,
      standard_tech_id: rigDraft.standard_tech_id || null,
      is_active: true,
      link_offering_id: !rigDraft.id && scope !== "general" ? scope : null,
    });
    if (!res.ok) {
      setActionBusy(false);
      onError(res.error);
      return;
    }
    const rigId = res.data.rig.id;
    const existing = findNoteForResource("rig", rigId);
    if (noteDraft.body.trim()) {
      await upsertServiceResourceNote({
        id: existing?.id,
        resource_type: "rig",
        resource_id: rigId,
        body: noteDraft.body.trim(),
        expires_on: noteDraft.expires_on.trim() || null,
      });
    } else if (existing?.id) {
      await dismissServiceResourceNote(existing.id);
    }
    setActionBusy(false);
    setSelectedRigId(null);
    setRigDraft(BLANK_RIG);
    setRigBaseline(JSON.stringify(BLANK_RIG));
    resetNoteDraft();
    await onReload();
    await loadNotes();
  }, [loadNotes, noteDraft, notes, onError, onReload, rigDraft, scope]);

  const saveTech = useCallback(async () => {
    if (!techDraft.display_name.trim()) {
      onError("Technician name is required.");
      return;
    }
    setActionBusy(true);
    onError(null);
    const tags = techDraft.skill_tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const res = await upsertServiceTech({
      id: techDraft.id,
      display_name: techDraft.display_name.trim(),
      phone: techDraft.phone.trim(),
      skill_tags: tags,
      is_active: true,
      link_offering_id: !techDraft.id && scope !== "general" ? scope : null,
    });
    if (!res.ok) {
      setActionBusy(false);
      onError(res.error);
      return;
    }
    const techId = res.data.tech.id;
    const existing = findNoteForResource("tech", techId);
    if (noteDraft.body.trim()) {
      await upsertServiceResourceNote({
        id: existing?.id,
        resource_type: "tech",
        resource_id: techId,
        body: noteDraft.body.trim(),
        expires_on: noteDraft.expires_on.trim() || null,
      });
    } else if (existing?.id) {
      await dismissServiceResourceNote(existing.id);
    }
    setActionBusy(false);
    setSelectedTechId(null);
    setTechDraft(BLANK_TECH);
    setTechBaseline(JSON.stringify(BLANK_TECH));
    resetNoteDraft();
    await onReload();
    await loadNotes();
  }, [loadNotes, noteDraft, notes, onError, onReload, scope, techDraft]);

  const dismissNote = useCallback(
    async (noteId: string) => {
      await dismissServiceResourceNote(noteId);
      const blank = { body: "", expires_on: "" };
      setNoteDraft(blank);
      setNoteBaseline(JSON.stringify(blank));
      await loadNotes();
    },
    [loadNotes]
  );

  const trySetScope = useCallback(
    (next: ServiceDefinitionScope, current: ServiceDefinitionScope, setter: (v: ServiceDefinitionScope) => void) => {
      if (next === current) return true;
      if (!confirmDiscardDirty(formDirty)) return false;
      setter(next);
      setSelectedRigId(null);
      setSelectedTechId(null);
      setRigDraft(BLANK_RIG);
      setTechDraft(BLANK_TECH);
      setRigBaseline(JSON.stringify(BLANK_RIG));
      setTechBaseline(JSON.stringify(BLANK_TECH));
      resetNoteDraft();
      return true;
    },
    [formDirty]
  );

  return {
    scopedRigs,
    scopedTechs,
    pagedRigs,
    pagedTechs,
    rigPage,
    setRigPage,
    techPage,
    setTechPage,
    rigPages,
    techPages,
    pageSize,
    setPageSize,
    selectedRigId,
    selectedTechId,
    selectRig,
    selectTech,
    rigDraft,
    setRigDraft,
    techDraft,
    setTechDraft,
    noteDraft,
    setNoteDraft,
    formDirty,
    actionBusy,
    saveRig,
    saveTech,
    notes,
    notesForResource,
    dismissNote,
    trySetScope,
    loadNotes,
  };
}
